import { pyRound } from "./pyRound";
import { COLUMN_RENAMING, getDisplay } from "./metadata";
import { EMPTY_FRAME, frameFromRows, isMissing, concatFrames } from "./frame";

export const FLUID_TYPES = [
  "Oil", "Gas", "Condensate", "Water", "Filtrate", "Synthetic",
  "Brine", "Mud", "Base oil", "Emulsion", "Oil/Water", "Condensate/Water", "Filter Cake",
];

export const PHASES = ["Reservoir Fluid", "Flashed Oil", "Flashed Gas"];
export const DATA_TYPES = ["mole", "wt"];

function get(obj, key, fallback) {
  return obj && Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : fallback;
}

function sampleBase(sample) {
  const location = get(sample, "location", {}) || {};
  return {
    ID: get(sample, "ID", ""),
    "Sample ID": get(sample, "fluidSampleID", ""),
    Asset: get(location, "asset", ""),
    Field: get(location, "field", ""),
    Reservoir: get(location, "reservoir", ""),
    Well: get(location, "well", ""),
    Source: get(sample, "source", ""),
    "Sample Kind": sample ? sample.fluidType : undefined,
  };
}

function renameRow(row, mapping) {
  const out = {};
  for (const [key, value] of Object.entries(row)) out[mapping[key] || key] = value;
  return out;
}

function roundColumns(rows, operations) {
  for (const row of rows) {
    for (const [column, digits] of operations) {
      if (column in row && !isMissing(row[column])) row[column] = pyRound(row[column], digits);
    }
  }
}

function digitsOf(tables, table, fields) {
  const out = {};
  for (const field of fields) out[field] = getDisplay(tables, table, field).digits;
  return out;
}

function atSaturation(row, satColumn, stepColumn) {
  const sat = row[satColumn];
  const step = row[stepColumn];
  if (isMissing(sat) || isMissing(step)) return false;
  return sat === step || sat === step + 1 || sat === step - 1;
}

export function mapCce(samples, tables) {
  const d = digitsOf(tables, "ccetests", [
    "saturationPressure", "testTemperature", "cceTestStepsstepPressure", "cceTestStepsrelativeVolumeRatio",
    "cceTestStepsgasZFactor", "cceTestStepsliquidFractionSat", "cceTestStepsoilDensity", "cceTestStepsoilCompressibility",
  ]);
  const rows = [];
  for (const sample of samples) {
    const base = sampleBase(sample);
    for (const test of get(sample, "ccetests", []) || []) {
      for (const step of get(test, "CCETestStep", []) || []) {
        rows.push(renameRow({
          ...base,
          saturationPressure: test.saturationPressure,
          testTemperature: test.testTemperature,
          cceTestStepsstepPressure: step.stepPressure,
          cceTestStepsrelativeVolumeRatio: step.relativeVolumeRatio,
          cceTestStepsoilCompressibility: step.oilCompressibility,
          cceTestStepsoilDensity: step.oilDensity,
          cceTestStepsgasZFactor: step.gasZFactor,
          cceTestStepsliquidFractionSat: step.liquidFractionSat,
        }, COLUMN_RENAMING.ccetests));
      }
    }
  }
  if (rows.length === 0) return EMPTY_FRAME;

  const filtered = rows.filter((row) => row["CCE Vr"] === 1);
  roundColumns(filtered, [
    ["Saturation Pressure (psia)", d.saturationPressure],
    ["CCE Test Temperature (F)", d.testTemperature],
    ["CCE stepPressure", d.cceTestStepsstepPressure],
    ["CCE Vr", d.cceTestStepsrelativeVolumeRatio],
    ["CCE gas Z Factor", d.cceTestStepsgasZFactor],
    ["CCE liquidFractionSat", d.cceTestStepsliquidFractionSat],
    ["CCE Oil Density (g/cc)", d.cceTestStepsoilDensity],
    ["Oil Compressibility (10-6 x psi-1)", d.cceTestStepsoilCompressibility],
  ]);
  return { columns: frameFromRows(rows).columns, rows: filtered };
}

export function mapDl(samples, tables) {
  const d = digitsOf(tables, "dltests", [
    "saturationPressure", "dlTestStepsstepNumber", "testTemperature", "dlTestStepsstepPressure",
    "dlTestStepsoilDensity", "dlTestStepsoilFormationVolumeFactor", "dlTestStepssolutionGORMeasured",
    "dlTestStepsgasZFactor", "dlTestStepsgasFormationVolumeFactor", "dlTestStepsgasGravity",
    "apiGravity", "residualOilDensity",
  ]);
  const rows = [];
  for (const sample of samples) {
    const base = sampleBase(sample);
    for (const test of get(sample, "dltests", []) || []) {
      for (const step of get(test, "dlTestSteps", []) || []) {
        rows.push(renameRow({
          ...base,
          saturationPressure: test.saturationPressure,
          testTemperature: test.testTemperature,
          dlTestStepsstepNumber: step.stepNumber,
          dlTestStepsstepPressure: step.stepPressure,
          dlTestStepsoilDensity: step.oilDensity,
          dlTestStepsoilFormationVolumeFactor: step.oilFormationVolumeFactor,
          dlTestStepssolutionGORMeasured: step.solutionGORMeasured,
          dlTestStepsgasZFactor: step.gasZFactor,
          dlTestStepsgasFormationVolumeFactor: step.gasFormationVolumeFactor,
          dlTestStepsgasGravity: step.gasGravity,
          apiGravity: test.apiGravity,
          residualOilDensity: test.residualOilDensity,
        }, COLUMN_RENAMING.dltests));
      }
    }
  }
  if (rows.length === 0) return EMPTY_FRAME;
  const columns = frameFromRows(rows).columns;

  roundColumns(rows, [["dl SaturationPressure", d.saturationPressure]]);
  const filtered = rows.filter((row) => atSaturation(row, "dl SaturationPressure", "dl StepPressure"));
  roundColumns(filtered, [
    ["dl Test Temperature (F)", d.testTemperature],
    ["dl StepPressure", d.dlTestStepsstepPressure],
    ["dl Oil Density (g/cc)", d.dlTestStepsoilDensity],
    ["dl Bo (bbl/stb)", d.dlTestStepsoilFormationVolumeFactor],
    ["dl Rs (scf/stb)", d.dlTestStepssolutionGORMeasured],
    ["dl gasZFactor", d.dlTestStepsgasZFactor],
    ["dl gasFormationVolumeFactor", d.dlTestStepsgasFormationVolumeFactor],
    ["dl gasGravity", d.dlTestStepsgasGravity],
    ["dl TestNumber", d.dlTestStepsstepNumber],
    ["API Gravity - DL Test", d.apiGravity],
    ["Residual Oil Density", d.residualOilDensity],
  ]);
  return { columns, rows: filtered };
}

export function mapViscosity(samples, tables) {
  const d = digitsOf(tables, "viscositytests", [
    "saturationPressure", "testTemperature", "viscosityTestStepstestNumber", "viscosityTestStepsstepPressure",
    "viscosityTestStepsoilViscosity", "gasViscosity", "oilGasViscosityRatio",
  ]);
  const rows = [];
  for (const sample of samples) {
    const base = sampleBase(sample);
    for (const test of get(sample, "viscositytests", []) || []) {
      for (const step of get(test, "viscosityTestSteps", []) || []) {
        rows.push(renameRow({
          ...base,
          saturationPressure: test.saturationPressure,
          testTemperature: test.testTemperature,
          viscosityTestStepstestNumber: test.testNumber,
          viscosityTestStepsstepPressure: step.stepPressure,
          viscosityTestStepsoilViscosity: step.oilViscosity,
          gasViscosity: step.gasViscosity,
          oilGasViscosityRatio: step.oilGasViscosityRatio,
        }, COLUMN_RENAMING.viscositytests));
      }
    }
  }
  if (rows.length === 0) return EMPTY_FRAME;
  const columns = frameFromRows(rows).columns;

  roundColumns(rows, [["viscosity SaturationPressure", d.saturationPressure]]);
  const filtered = rows.filter((row) => atSaturation(row, "viscosity SaturationPressure", "viscosity StepPressure"));
  roundColumns(filtered, [
    ["viscosity Test Temperature (F)", d.testTemperature],
    ["viscosity StepPressure", d.viscosityTestStepsstepPressure],
    ["Oil Viscosity (cP)", d.viscosityTestStepsoilViscosity],
    ["viscosity TestNumber", d.viscosityTestStepstestNumber],
    ["Gas Viscosity (cP)", d.gasViscosity],
    ["Viscosity Ratio", d.oilGasViscosityRatio],
  ]);
  return { columns, rows: filtered };
}

export function mapCvd(samples, tables) {
  const d = digitsOf(tables, "cvdtests", [
    "saturationPressure", "testTemperature", "cvdTestStepsstepNumber", "CVDTestStepstepPressure",
    "CVDTestStepgasZFactor", "CVDTestStepliquidFraction", "CVDTestStepcumulativeFluidProducedFraction",
  ]);
  const rows = [];
  for (const sample of samples) {
    const base = sampleBase(sample);
    for (const test of get(sample, "cvdtests", []) || []) {
      for (const step of get(test, "cvdTestSteps", []) || []) {
        rows.push(renameRow({
          ...base,
          saturationPressure: test.saturationPressure,
          testTemperature: test.testTemperature,
          cvdTestStepsstepNumber: step.stepNumber,
          CVDTestStepstepPressure: step.stepPressure,
          CVDTestStepliquidFraction: step.liquidFraction,
          CVDTestStepgasZFactor: step.gasZFactor,
          CVDTestStepcumulativeFluidProducedFraction: step.cumulativeFluidProducedFraction,
        }, COLUMN_RENAMING.cvdtests));
      }
    }
  }
  if (rows.length === 0) return EMPTY_FRAME;
  const columns = frameFromRows(rows).columns;

  roundColumns(rows, [["cvd SaturationPressure", d.saturationPressure]]);
  const filtered = rows.filter((row) => atSaturation(row, "cvd SaturationPressure", "cvd StepPressure"));
  roundColumns(filtered, [
    ["cvd Test Temperature (F)", d.testTemperature],
    ["cvd StepPressure", d.CVDTestStepstepPressure],
    ["cvd Gas Z Factor", d.CVDTestStepgasZFactor],
    ["cvd LiquidFraction", d.CVDTestStepliquidFraction],
    ["cvd CumulativeFluidProducedFraction", d.CVDTestStepcumulativeFluidProducedFraction],
    ["cvd TestNumber", d.cvdTestStepsstepNumber],
  ]);
  return { columns, rows: filtered };
}

const COMPOSITION_DIGIT_KEYS = [
  "flashGOR", "mudContaminated_FlashGOR", "flashGOR_ToTemperature", "liquidDensity", "oilAPIGravity",
  "oilMolecularWeight", "avgMolecularWeight", "liquid_MoleRatio", "vapor_MoleRatio",
  "compositionoverall_MoleFraction", "compositionoverall_MassFraction", "compositionliquid_MoleFraction",
  "compositionliquid_MassFraction", "compositionvapor_MoleFraction", "compositionvapor_MassFraction",
  "compositionoverall_MW", "compositionoverall_Density", "gasGravity", "gasDensity", "gasMolecularWeight",
  "gasZFactor", "grossEnergyContentPerUnitMass", "grossEnergyContentPerUnitVolume",
  "grossEnergyContentPerUnitVolumeDry", "oilFormationVolumeFactor",
  "oilFormationVolumeFactor_FlashToTemperature", "oilShrinkageFactor",
];

export function formatComponent(comp) {
  if (!comp) return comp;
  if (comp.startsWith("n-")) return "n-" + comp.slice(2).toUpperCase();
  if (comp.startsWith("i-")) return "i-" + comp.slice(2).toUpperCase();
  if (comp.includes("+")) {
    const parts = comp.split("+");
    if (parts.length === 2) return parts[0].toUpperCase() + "+" + parts[1];
  }
  if (comp.includes(" MW")) return formatComponent(comp.replace(/ MW/g, "")) + " MW";
  if (comp.includes(" Density")) return formatComponent(comp.replace(/ Density/g, "")) + " Density";
  return comp.toUpperCase();
}

export function selectBo(test, digits) {
  if (!isMissing(test.oilFormationVolumeFactor)) {
    return pyRound(test.oilFormationVolumeFactor, digits.oilFormationVolumeFactor);
  }
  if (!isMissing(test.oilFormationVolumeFactor_FlashToTemperature)) {
    return pyRound(test.oilFormationVolumeFactor_FlashToTemperature, digits.oilFormationVolumeFactor_FlashToTemperature);
  }
  return null;
}

export function selectGor(test, digits) {
  const contamination = String(test.contaminationType ?? "").toLowerCase();
  const flash = test.flashGOR;
  const mud = test.mudContaminated_FlashGOR;
  const toTemp = test.flashGOR_ToTemperature;

  let value;
  let key;
  if (!isMissing(toTemp) && isMissing(flash) && isMissing(mud)) {
    value = toTemp;
    key = "flashGOR_ToTemperature";
  } else if (contamination === "contaminated" && !isMissing(mud)) {
    value = mud;
    key = "mudContaminated_FlashGOR";
  } else {
    value = flash;
    key = "flashGOR";
  }
  return isMissing(value) ? value : pyRound(value, digits[key]);
}

function roundOrPass(value, digits) {
  return isMissing(value) ? value : pyRound(value, digits);
}

function selectCompositionTests(tests, applyFilter) {
  if (!applyFilter) return tests;
  const matching = tests.filter((t) =>
    ["Contaminated", "BTEX"].includes(get(t, "compositionType", "")) || get(t, "recombinationType", "") === "Physical"
  );
  return matching.length ? matching : tests;
}

export function mapComposition(samples, tables, fluidType, applyCompositionFilter = true) {
  const digits = digitsOf(tables, "compositiontests", COMPOSITION_DIGIT_KEYS);
  const r = (value, key) => roundOrPass(value, digits[key]);

  const tablesByPhase = {
    reservoirMole: new Map(), reservoirWt: new Map(),
    oilMole: new Map(), oilWt: new Map(),
    gasMole: new Map(), gasWt: new Map(),
  };

  for (const sample of samples) {
    const sampleFluidType = sample.fluidType;
    if (fluidType && sampleFluidType !== fluidType) continue;

    const tests = get(sample, "compositiontests", []) || [];
    if (tests.length === 0) continue;

    const loc = get(sample, "location", {}) || {};
    const ID = get(sample, "ID", "");
    const sampleId = get(sample, "fluidSampleID", "");
    const source = get(sample, "source", "");

    for (const test of selectCompositionTests(tests, applyCompositionFilter)) {
      const liquidMoleRatio = test.liquid_moleRatio;
      const vaporMoleRatio = test.vapor_mole_ratio;
      const baseRecord = {
        ID,
        "Sample ID": sampleId,
        Asset: get(loc, "asset", ""),
        Field: get(loc, "field", ""),
        Reservoir: get(loc, "reservoir", ""),
        Well: get(loc, "well", ""),
        Source: source,
        "Sample Kind": sampleFluidType,
        compositionType: test.compositionType,
        recombinationType: test.recombinationType,
        "Fluid Classification": get(sample, "fluidClassification", ""),
        "Sample Type": get(sample, "sampleType", ""),
        DepthMD: get(sample, "depthMD", ""),
        DepthTVDss: get(sample, "depthTVDss", ""),
        "Top DepthMD": get(sample, "topPerforationDepthMD", ""),
        "Bottom DepthMD": get(sample, "bottomPerforationDepthMD", ""),
        "Mid DepthMD": get(sample, "midPerforationDepthMD", ""),
        "Reservoir Temperature (F)": get(sample, "reservoirTemperature", ""),
        "GOR (scf/stb)": selectGor(test, digits),
        "STO Density (g/cc)": r(test.liquidDensity, "liquidDensity"),
        "API deg": r(test.oilAPIGravity, "oilAPIGravity"),
        "STO MW (g/mol)": r(test.oilMolecularWeight, "oilMolecularWeight"),
        "Oil Molar Mass (g/mol)": r(test.avgMolecularWeight, "avgMolecularWeight"),
        "Gas Gravity": r(test.gasGravity, "gasGravity"),
        "Gas Density (g/cm3)": r(test.gasDensity, "gasDensity"),
        "Gas MW (g/mol)": r(test.gasMolecularWeight, "gasMolecularWeight"),
        "Gas Z Factor": r(test.gasZFactor, "gasZFactor"),
        "Gross Energy Mass Content (BTU/scf)": r(test.grossEnergyContentPerUnitMass, "grossEnergyContentPerUnitMass"),
        "Gross Energy Volume Content (BTU/scf)": r(test.grossEnergyContentPerUnitVolume, "grossEnergyContentPerUnitVolume"),
        "Dry Gross Energy Volume Content (BTU/scf)": r(test.grossEnergyContentPerUnitVolumeDry, "grossEnergyContentPerUnitVolumeDry"),
        "Flash Oil FVF (bbl/stb)": r(test.oilFormationVolumeFactor, "oilFormationVolumeFactor"),
        Bo: selectBo(test, digits),
        "Flash Oil Shrinkage Factor (bbl/stb)": r(test.oilShrinkageFactor, "oilShrinkageFactor"),
      };
      const key = JSON.stringify([ID, sampleId, source]);
      const record = (table, extras) => {
        if (!table.has(key)) table.set(key, { ...baseRecord, ...(extras ? extras() : {}) });
        return table.get(key);
      };
      const liquidExtras = (withMass) => () => {
        const ratio = r(liquidMoleRatio, "liquid_MoleRatio");
        return withMass
          ? { "Liquid Phase Molar Fraction": ratio, "Liquid Phase Mass Fraction": ratio }
          : { "Liquid Phase Molar Fraction": ratio };
      };
      const vaporExtras = (withMass) => () => {
        const ratio = r(vaporMoleRatio, "vapor_MoleRatio");
        return withMass
          ? { "Vapor Phase Molar Fraction": ratio, "Vapor Phase Mass Fraction": ratio }
          : { "Vapor Phase Molar Fraction": ratio };
      };

      const applyFractions = (item, comp) => {
        if (!isMissing(item.overall_MoleFraction)) {
          record(tablesByPhase.reservoirMole)[comp] = r(item.overall_MoleFraction, "compositionoverall_MoleFraction");
        }
        if (!isMissing(item.overall_MassFraction)) {
          record(tablesByPhase.reservoirWt)[comp] = r(item.overall_MassFraction, "compositionoverall_MassFraction");
        }
        if (!isMissing(item.liquid_MoleFraction)) {
          record(tablesByPhase.oilMole, liquidExtras(false))[comp] = r(item.liquid_MoleFraction, "compositionliquid_MoleFraction");
        }
        if (!isMissing(item.liquid_MassFraction)) {
          record(tablesByPhase.oilWt, liquidExtras(true))[comp] = r(item.liquid_MassFraction, "compositionliquid_MassFraction");
        }
        if (!isMissing(item.vapor_MoleFraction)) {
          record(tablesByPhase.gasMole, vaporExtras(false))[comp] = r(item.vapor_MoleFraction, "compositionvapor_MoleFraction");
        }
        if (!isMissing(item.vapor_MassFraction)) {
          record(tablesByPhase.gasWt, vaporExtras(true))[comp] = r(item.vapor_MassFraction, "compositionvapor_MassFraction");
        }
      };

      for (const item of get(test, "composition", []) || []) {
        const comp = formatComponent(get(item, "fluidComponentReference", null));
        if (!comp) continue;
        applyFractions(item, comp);
      }

      for (const item of get(test, "compositionProperties", []) || []) {
        const comp = formatComponent(get(item, "fluidComponentReference", null));
        if (!comp) continue;
        applyFractions(item, comp);
        if (!isMissing(item.overall_MW)) {
          const value = r(item.overall_MW, "compositionoverall_MW");
          record(tablesByPhase.reservoirMole)[`${comp} MW (g/mol)`] = value;
          record(tablesByPhase.reservoirWt)[`${comp} MW (g/mol)`] = value;
        }
        if (!isMissing(item.overall_Density)) {
          const value = r(item.overall_Density, "compositionoverall_Density");
          record(tablesByPhase.reservoirMole)[`${comp} Density (g/cc)`] = value;
          record(tablesByPhase.reservoirWt)[`${comp} Density (g/cc)`] = value;
        }
      }
    }
  }

  const toFrame = (map) => (map.size ? frameFromRows([...map.values()]) : EMPTY_FRAME);
  return {
    "Reservoir Fluid": { mole: toFrame(tablesByPhase.reservoirMole), wt: toFrame(tablesByPhase.reservoirWt) },
    "Flashed Oil": { mole: toFrame(tablesByPhase.oilMole), wt: toFrame(tablesByPhase.oilWt) },
    "Flashed Gas": { mole: toFrame(tablesByPhase.gasMole), wt: toFrame(tablesByPhase.gasWt) },
  };
}

export function mapAllCompositions(samples, tables, applyCompositionFilter = true) {
  const perType = FLUID_TYPES.map((fluidType) => mapComposition(samples, tables, fluidType, applyCompositionFilter));
  const out = {};
  for (const phase of PHASES) {
    out[phase] = {};
    for (const dataType of DATA_TYPES) {
      out[phase][dataType] = concatFrames(perType.map((t) => t[phase][dataType]));
    }
  }
  return out;
}
